import * as React from 'react'

import { HorizontalAlignment } from '@thg-commerce/enterprise-core'
import { Button, ButtonEmphases } from '@thg-commerce/gravity-elements'

import { LogoPicture, LogoProps } from './LogoImage'
import {
  Container,
  LogoItem,
  LogoWrapper,
  ScrollingLogoWrapper,
  StyledGrid,
  StyledGridItem,
  SubTitle,
  TextOnlyStaticWrapper,
  Title,
} from './styles'

export interface LogoImageCollectionProps {
  title?: string
  subtitle?: string
  logos: LogoProps[]
  horizontalAlignment?: HorizontalAlignment
  itemHorizontalAlignment?: HorizontalAlignment
  cta?: CtaProps
  customBrandNames?: string[]
  enableTextOnlyCarouselProp?: string
}

interface CtaProps {
  text: string
  link: string
  emphasis?: ButtonEmphases | 'high'
}

export const LogoImageCollection = ({
  horizontalAlignment = 'center',
  ...props
}: LogoImageCollectionProps) => {
  const enableTextOnlyCarousel = props.enableTextOnlyCarouselProp === 'true'
  const oddTotalItems = props.logos.length % 2 !== 0

  // Extract brand names handling logic
  const getBrandNamesForDisplay = (brandNames: string[]) => {
    if (!enableTextOnlyCarousel || brandNames.length <= 4) {
      return brandNames
    }

    const repetitions = 3
    let result: string[] = []
    for (let i = 0; i < repetitions; i += 1) {
      result = [...result, ...brandNames]
    }
    return result
  }

  // Extract logo item rendering logic
  const renderLogoItem = (
    logo: LogoProps,
    brandName?: string,
    index: number = 0,
  ) => (
    <LogoItem
      key={brandName ? `${brandName}-${index}` : index}
      oddTotalItems={oddTotalItems}
    >
      <LogoPicture
        key={brandName ? `${brandName}-${index}` : index}
        {...logo}
        brandName={brandName}
        textOnlyCarousel={enableTextOnlyCarousel}
        horizontalAlignment={
          props.itemHorizontalAlignment || horizontalAlignment
        }
        widgetIndex={index}
      />
    </LogoItem>
  )

  // Handle logo items creation
  const createLogoItems = () => {
    if (!enableTextOnlyCarousel) {
      return props.logos.map((logo, index) =>
        renderLogoItem(logo, undefined, index),
      )
    }

    const brandNamesToUse = props.customBrandNames || []
    const brandNamesForDisplay = getBrandNamesForDisplay(brandNamesToUse)

    return brandNamesForDisplay.map((brandName, index) => {
      const logo = props.logos[index % props.logos.length] || {}
      return renderLogoItem(logo, brandName, index)
    })
  }

  // Handle wrapper selection
  const wrapLogoItems = (items: JSX.Element[]) => {
    if (!enableTextOnlyCarousel) {
      return items
    }

    const brandNamesToUse = props.customBrandNames || []
    const shouldAnimate = brandNamesToUse.length > 4

    return shouldAnimate ? (
      <ScrollingLogoWrapper>{items}</ScrollingLogoWrapper>
    ) : (
      <TextOnlyStaticWrapper horizontalAlignment={horizontalAlignment}>
        {items}
      </TextOnlyStaticWrapper>
    )
  }

  const logoItems = createLogoItems()
  const wrappedLogos = wrapLogoItems(logoItems)

  const colTitleStart = [
    { left: 1, center: 3, right: 5 },
    { left: 1, center: 4, right: 7 },
  ]

  return (
    <Container horizontalAlignment={horizontalAlignment}>
      {(props.title || props.subtitle) && (
        <StyledGrid columns={12}>
          <StyledGridItem
            colSpan={[12, 8, 6, 6]}
            colStart={
              horizontalAlignment
                ? [
                    1,
                    colTitleStart[0][horizontalAlignment],
                    colTitleStart[1][horizontalAlignment],
                    colTitleStart[1][horizontalAlignment],
                  ]
                : 1
            }
          >
            {props.title && <Title>{props.title}</Title>}
            {props.subtitle && <SubTitle>{props.subtitle}</SubTitle>}
          </StyledGridItem>
        </StyledGrid>
      )}
      <LogoWrapper
        horizontalAlignment={horizontalAlignment}
        textOnlyCarousel={enableTextOnlyCarousel}
        hasTitle={!!(props.title || props.subtitle)}
      >
        {wrappedLogos}
      </LogoWrapper>
      {props?.cta && (
        <Button href={props.cta.link} emphasis={props.cta?.emphasis}>
          {props.cta.text}
        </Button>
      )}
    </Container>
  )
}
